import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, lazy, Suspense, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/db";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, FileText, Plus, Trash2, Mail, AlertCircle, Download, Copy, ListChecks, Users, Sparkles, FilePen, RefreshCw, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

const TiptapEditor = lazy(() =>
  import("@/components/TiptapEditor").then((m) => ({ default: m.TiptapEditor })),
);
const TiptapFallback = () => (
  <div className="border rounded-md min-h-[320px] animate-pulse bg-muted/30" />
);

export const Route = createFileRoute("/mailing")({ component: () => <Guard module="mailing"><MailingPage /></Guard> });

const INACTIVITY_YEARS = 3;

// Types partagés
interface MailingList {
  id: string;
  name: string;
  description: string | null;
  kind: "dynamic_active" | "dynamic_inactive" | "manual";
}

// Hook utilitaire : récupère la résolution d'une liste -> emails
function useMembersWithStatus() {
  return useQuery({
    queryKey: ["members-with-status"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, first_name, last_name, email, archived, memberships(season)")
        .order("last_name");
      if (error) throw error;
      const currentYear = new Date().getFullYear();
      const threshold = currentYear - INACTIVITY_YEARS;
      return (data ?? []).map((m: any) => {
        const lastSeason = (m.memberships ?? []).reduce((acc: number, x: any) => {
          const n = Number(x.season);
          return Number.isFinite(n) && n > acc ? n : acc;
        }, 0);
        const isInactive = !!m.archived || lastSeason < threshold;
        return { ...m, _isInactive: isInactive };
      });
    },
  });
}

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  recipient_emails: string[] | string | null;
  is_mass_mailing: number | boolean;
  smtp_profile_id: string | null;
  status: "draft" | "sending" | "sent" | "failed";
  sent_at: string | null;
  last_error: string | null;
  created_at: string;
}

function normalizeRecipients(v: EmailCampaign["recipient_emails"]): string[] {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    try { const j = JSON.parse(v); return Array.isArray(j) ? j : []; } catch { return []; }
  }
  return [];
}

function MailingPage() {
  const [activeTab, setActiveTab] = useState("compose");
  const [draftToLoad, setDraftToLoad] = useState<EmailCampaign | null>(null);

  const openDraft = (c: EmailCampaign) => {
    setDraftToLoad(c);
    setActiveTab("compose");
  };

  return (
    <>
      <PageHeader title="Mails" description="Envoyer des emails aux adhérents et gérer vos modèles & listes" />
      <Card className="p-4 mb-4 border-warning bg-warning/5">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Configuration SMTP requise :</strong> les paramètres de votre serveur mail doivent être renseignés
            dans <em>Administration → Paramètres SMTP</em>. L'envoi effectif sera branché lors de la mise en production
            sur votre hébergeur (l'environnement d'aperçu Lovable Cloud ne peut pas se connecter à un SMTP classique).
          </div>
        </div>
      </Card>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="compose"><Send className="h-4 w-4 mr-1.5" />Composer</TabsTrigger>
          <TabsTrigger value="drafts"><FilePen className="h-4 w-4 mr-1.5" />Brouillons</TabsTrigger>
          <TabsTrigger value="lists"><ListChecks className="h-4 w-4 mr-1.5" />Listes de diffusion</TabsTrigger>
          <TabsTrigger value="templates"><FileText className="h-4 w-4 mr-1.5" />Modèles</TabsTrigger>
          <TabsTrigger value="history"><Mail className="h-4 w-4 mr-1.5" />Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="compose" className="mt-4">
          <ComposeTab draft={draftToLoad} onDraftConsumed={() => setDraftToLoad(null)} />
        </TabsContent>
        <TabsContent value="drafts" className="mt-4"><DraftsTab onEdit={openDraft} /></TabsContent>
        <TabsContent value="lists" className="mt-4"><ListsTab /></TabsContent>
        <TabsContent value="templates" className="mt-4"><TemplatesTab /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab /></TabsContent>
      </Tabs>
    </>
  );
}

function RichTextEditor(props: { value: string; onChange: (html: string) => void }) {
  return (
    <Suspense fallback={<TiptapFallback />}>
      <TiptapEditor {...props} />
    </Suspense>
  );
}

function ComposeTab({ draft, onDraftConsumed }: { draft: EmailCampaign | null; onDraftConsumed: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [draftId, setDraftId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("<p>Bonjour,</p><p></p>");
  const [mode, setMode] = useState<"all" | "list" | "manual">("all");
  const [selectedListId, setSelectedListId] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [smtpProfileId, setSmtpProfileId] = useState<string>("");
  const [extraRecipients, setExtraRecipients] = useState<string>("");

  // Charge un brouillon dans le formulaire si demandé
  useEffect(() => {
    if (!draft) return;
    setDraftId(draft.id);
    setSubject(draft.subject ?? "");
    setBody(draft.body_html ?? "<p></p>");
    setSmtpProfileId(draft.smtp_profile_id ?? "");
    setMode("manual");
    setSelectedIds(new Set());
    setExtraRecipients(normalizeRecipients(draft.recipient_emails).join(", "));
    onDraftConsumed();
    toast.success(`Brouillon "${draft.subject || draft.name}" chargé`);
  }, [draft, onDraftConsumed]);

  const resetForm = () => {
    setDraftId(null);
    setSubject("");
    setBody("<p>Bonjour,</p><p></p>");
    setExtraRecipients("");
    setSelectedIds(new Set());
  };

  const { data: members = [] } = useMembersWithStatus();
  const { data: templates = [] } = useQuery({
    queryKey: ["email_templates"],
    queryFn: async () => (await supabase.from("email_templates").select("*").order("name")).data ?? [],
  });
  const { data: smtpProfiles = [] } = useQuery({
    queryKey: ["smtp_profiles"],
    queryFn: async () => (await supabase.from("smtp_profiles").select("*").order("sort_order")).data ?? [],
  });
  const { data: lists = [] } = useQuery<MailingList[]>({
    queryKey: ["mailing_lists"],
    queryFn: async () => ((await supabase.from("mailing_lists").select("*").order("kind").order("name")).data as any) ?? [],
  });
  const { data: listMembers = [] } = useQuery({
    queryKey: ["mailing_list_members", selectedListId],
    queryFn: async () => {
      if (!selectedListId) return [];
      return ((await supabase.from("mailing_list_members").select("member_id").eq("list_id", selectedListId)).data ?? []) as { member_id: string }[];
    },
    enabled: !!selectedListId && mode === "list",
  });

  useEffect(() => {
    if (smtpProfileId === "" && smtpProfiles.length > 0) {
      const def = smtpProfiles.find((p: any) => p.is_default) ?? smtpProfiles[0];
      if (def?.id) setSmtpProfileId(def.id);
    }
  }, [smtpProfiles, smtpProfileId]);

  const extraList = useMemo(() => {
    return extraRecipients
      .split(/[,;\s\n]+/)
      .map((s) => s.trim())
      .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
  }, [extraRecipients]);

  const recipients = useMemo(() => {
    let base: string[] = [];
    if (mode === "all") {
      base = members.filter((m: any) => m.email && !m._isInactive).map((m: any) => m.email!);
    } else if (mode === "manual") {
      base = members.filter((m: any) => selectedIds.has(m.id) && m.email).map((m: any) => m.email!);
    } else {
      const list = lists.find((l) => l.id === selectedListId);
      if (list) {
        if (list.kind === "dynamic_active") {
          base = members.filter((m: any) => m.email && !m._isInactive).map((m: any) => m.email!);
        } else if (list.kind === "dynamic_inactive") {
          base = members.filter((m: any) => m.email && m._isInactive).map((m: any) => m.email!);
        } else {
          const ids = new Set(listMembers.map((x) => x.member_id));
          base = members.filter((m: any) => ids.has(m.id) && m.email).map((m: any) => m.email!);
        }
      }
    }
    return Array.from(new Set([...base, ...extraList]));
  }, [mode, members, selectedIds, selectedListId, lists, listMembers, extraList]);

  const payload = () => ({
    name: subject || "(sans nom)",
    subject,
    body_html: body,
    is_mass_mailing: mode !== "manual",
    recipient_emails: recipients,
    smtp_profile_id: smtpProfileId || null,
    created_by: user?.id,
  });

  const saveDraft = useMutation({
    mutationFn: async () => {
      if (!subject) throw new Error("L'objet est requis pour enregistrer un brouillon");
      if (draftId) {
        await api.patch(`/api/email-campaigns/${draftId}`, payload());
      } else {
        const res = await api.post<{ data: EmailCampaign }>("/api/email-campaigns", { ...payload(), status: "draft" });
        const newId = (res as any)?.data?.id ?? (res as any)?.id;
        if (newId) setDraftId(newId);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_campaigns"] });
      toast.success("Brouillon enregistré");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const send = useMutation({
    mutationFn: async () => {
      if (draftId) {
        // Met à jour le brouillon puis l'envoie
        await api.patch(`/api/email-campaigns/${draftId}`, payload());
        const res = await api.post<{ send_error: string | null; data: EmailCampaign }>(`/api/email-campaigns/${draftId}/send`);
        if ((res as any)?.send_error) throw new Error((res as any).send_error);
      } else {
        const res = await api.post<{ send_error: string | null; data: EmailCampaign }>("/api/email-campaigns", payload());
        if ((res as any)?.send_error) throw new Error((res as any).send_error);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_campaigns"] });
      toast.success(`Campagne envoyée à ${recipients.length} destinataire${recipients.length > 1 ? "s" : ""}.`);
      resetForm();
    },
    onError: (e: any) => {
      qc.invalidateQueries({ queryKey: ["email_campaigns"] });
      toast.error(e.message || "Échec de l'envoi");
    },
  });

  const loadTemplate = (id: string) => {
    const t: any = templates.find((x: any) => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.body_html); toast.success(`Modèle "${t.name}" chargé`); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <Card className="p-6 space-y-4">
          {smtpProfiles.length > 0 && (
            <div className="space-y-1.5">
              <Label>Expéditeur</Label>
              <select
                className="w-full border rounded-md h-9 px-3 bg-background"
                value={smtpProfileId}
                onChange={(e) => setSmtpProfileId(e.target.value)}
              >
                {smtpProfiles.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.role_name}
                    {p.from_email ? ` — ${p.from_email}` : ""}
                    {p.is_default ? "  (défaut)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {smtpProfiles.length === 0 && (
            <Card className="p-3 bg-warning/10 border-warning/30 text-sm flex gap-2">
              <AlertCircle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
              <div>
                Aucun profil SMTP configuré. Rendez-vous dans <em>Administration → Paramètres SMTP</em>
                pour créer une adresse d'envoi.
              </div>
            </Card>
          )}
          {templates.length > 0 && (
            <div className="space-y-1.5">
              <Label>Charger un modèle</Label>
              <select className="w-full border rounded-md h-9 px-3 bg-background" onChange={(e) => e.target.value && loadTemplate(e.target.value)} defaultValue="">
                <option value="">— Choisir un modèle —</option>
                {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Objet *</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} required /></div>
          <div className="space-y-1.5"><Label>Message *</Label><RichTextEditor value={body} onChange={setBody} /></div>
        </Card>
      </div>
      <div className="space-y-4">
        <Card className="p-6">
          <h3 className="font-medium mb-3">Destinataires</h3>
          <div className="space-y-2 mb-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={mode === "all"} onChange={() => setMode("all")} />
              <span>Tous les adhérents actifs</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={mode === "list"} onChange={() => setMode("list")} />
              <span>Une liste de diffusion</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={mode === "manual"} onChange={() => setMode("manual")} />
              <span>Sélection manuelle</span>
            </label>
          </div>

          {mode === "list" && (
            <div className="mb-3">
              <select
                className="w-full border rounded-md h-9 px-3 bg-background text-sm"
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
              >
                <option value="">— Choisir une liste —</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} {l.kind !== "manual" ? "(dynamique)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === "manual" && (
            <div className="max-h-72 overflow-y-auto space-y-1 border rounded-md p-2">
              {members.map((m: any) => (
                <label key={m.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-muted/40 px-1 rounded">
                  <Checkbox checked={selectedIds.has(m.id)} onCheckedChange={(v) => {
                    const n = new Set(selectedIds);
                    if (v) n.add(m.id); else n.delete(m.id);
                    setSelectedIds(n);
                  }} />
                  <span className="truncate">
                    {m.first_name} {m.last_name}
                    {m._isInactive && <Badge variant="outline" className="ml-1 text-[10px]">inactif</Badge>}
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t space-y-1.5">
            <Label className="text-sm">Destinataires additionnels</Label>
            <Textarea
              value={extraRecipients}
              onChange={(e) => setExtraRecipients(e.target.value)}
              placeholder="email1@exemple.com, email2@exemple.com"
              rows={3}
              className="text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Séparés par virgule, point-virgule ou retour à la ligne. Indépendant des listes.
              {extraRecipients && (
                <> {" "}<Badge variant="outline" className="ml-1">{extraList.length} valide{extraList.length > 1 ? "s" : ""}</Badge></>
              )}
            </p>
          </div>

          <div className="mt-3 text-sm text-muted-foreground">
            <Badge variant="secondary">{recipients.length}</Badge> destinataire{recipients.length > 1 ? "s" : ""} au total
            {draftId && <Badge variant="outline" className="ml-2">Brouillon en cours</Badge>}
          </div>
          {(() => {
            const blockers: string[] = [];
            if (!subject) blockers.push("renseigner un objet");
            if (recipients.length === 0) blockers.push("ajouter au moins un destinataire");
            if (smtpProfiles.length === 0) blockers.push("configurer un profil SMTP (Administration → SMTP)");
            const blocked = blockers.length > 0;
            return (
              <>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!subject) { toast.error("Renseignez un objet pour enregistrer le brouillon"); return; }
                      saveDraft.mutate();
                    }}
                    disabled={saveDraft.isPending}
                  >
                    <FilePen className="h-4 w-4 mr-2" />Brouillon
                  </Button>
                  <Button
                    onClick={() => {
                      if (blocked) { toast.error("Avant d'envoyer : " + blockers.join(", ")); return; }
                      toast.info(`Envoi en cours vers ${recipients.length} destinataire${recipients.length > 1 ? "s" : ""}…`);
                      send.mutate();
                    }}
                    disabled={send.isPending}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {send.isPending ? "Envoi…" : "Envoyer"}
                  </Button>
                </div>
                {blocked && (
                  <p className="text-xs text-warning mt-2 flex gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>Pour envoyer : {blockers.join(" · ")}.</span>
                  </p>
                )}
              </>
            );
          })()}
          {draftId && (
            <Button variant="ghost" size="sm" className="w-full mt-2" onClick={resetForm}>
              Nouveau mail (abandonner ce brouillon)
            </Button>
          )}
          <div className="mt-4 pt-4 border-t space-y-2">
            <p className="text-xs text-muted-foreground">Exporter pour un client de messagerie externe :</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={() => {
                const emailSet = new Set(recipients);
                const rows = members.filter((m: any) => m.email && emailSet.has(m.email));
                if (rows.length === 0) return toast.error("Aucun destinataire");
                const csv = "Prénom,Nom,Email\n" + rows.map((m: any) =>
                  `"${(m.first_name ?? "").replace(/"/g, '""')}","${(m.last_name ?? "").replace(/"/g, '""')}","${m.email}"`
                ).join("\n");
                const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `adherents-emails-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(`${rows.length} adresse${rows.length > 1 ? "s" : ""} exportée${rows.length > 1 ? "s" : ""}`);
              }} disabled={recipients.length === 0}>
                <Download className="h-4 w-4 mr-1.5" />CSV
              </Button>
              <Button variant="outline" size="sm" onClick={async () => {
                if (recipients.length === 0) return toast.error("Aucun destinataire");
                const text = recipients.join(", ");
                try {
                  await navigator.clipboard.writeText(text);
                  toast.success(`${recipients.length} email${recipients.length > 1 ? "s" : ""} copié${recipients.length > 1 ? "s" : ""} dans le presse-papiers`);
                } catch {
                  toast.error("Impossible de copier");
                }
              }} disabled={recipients.length === 0}>
                <Copy className="h-4 w-4 mr-1.5" />Copier
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ============ LISTES DE DIFFUSION ============
function ListsTab() {
  const qc = useQueryClient();
  const { hasAccess } = useAuth();
  const canWrite = hasAccess("mailing", "write");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ id?: string; name: string; description: string }>({ name: "", description: "" });
  const [managingList, setManagingList] = useState<MailingList | null>(null);

  const { data: lists = [] } = useQuery<MailingList[]>({
    queryKey: ["mailing_lists"],
    queryFn: async () => ((await supabase.from("mailing_lists").select("*").order("kind").order("name")).data as any) ?? [],
  });

  const { data: members = [] } = useMembersWithStatus();

  // Compteurs : pour les dynamiques on calcule, pour les manuelles on requête
  const { data: manualCounts = {} } = useQuery({
    queryKey: ["mailing_list_counts"],
    queryFn: async () => {
      const { data } = await supabase.from("mailing_list_members").select("list_id");
      const counts: Record<string, number> = {};
      for (const r of data ?? []) counts[r.list_id] = (counts[r.list_id] ?? 0) + 1;
      return counts;
    },
  });

  const dynamicActive = members.filter((m: any) => m.email && !m._isInactive).length;
  const dynamicInactive = members.filter((m: any) => m.email && m._isInactive).length;

  const save = useMutation({
    mutationFn: async () => {
      if (!editing.name.trim()) throw new Error("Nom requis");
      if (editing.id) {
        const { error } = await supabase.from("mailing_lists").update({
          name: editing.name, description: editing.description || null,
        }).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("mailing_lists").insert({
          name: editing.name, description: editing.description || null, kind: "manual",
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mailing_lists"] });
      toast.success("Liste enregistrée");
      setOpen(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("mailing_lists").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mailing_lists"] });
      qc.invalidateQueries({ queryKey: ["mailing_list_counts"] });
      toast.success("Liste supprimée");
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">
          Les listes <strong>dynamiques</strong> sont recalculées à chaque envoi. Les listes <strong>manuelles</strong> contiennent une sélection d'adhérents.
        </p>
        {canWrite && (
          <Button onClick={() => { setEditing({ name: "", description: "" }); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />Nouvelle liste
          </Button>
        )}
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Destinataires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lists.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Aucune liste</TableCell></TableRow>}
            {lists.map((l) => {
              const count = l.kind === "dynamic_active"
                ? dynamicActive
                : l.kind === "dynamic_inactive"
                ? dynamicInactive
                : (manualCounts[l.id] ?? 0);
              return (
                <TableRow key={l.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    {l.kind !== "manual" && <Sparkles className="h-3.5 w-3.5 text-primary" />}
                    {l.name}
                  </TableCell>
                  <TableCell>
                    {l.kind === "manual" ? (
                      <Badge variant="outline">Manuelle</Badge>
                    ) : (
                      <Badge variant="secondary">Dynamique</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{l.description ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary">{count}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {l.kind === "manual" && canWrite && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setManagingList(l)} title="Gérer les membres">
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing({ id: l.id, name: l.name, description: l.description ?? "" }); setOpen(true); }}>
                          Modifier
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { if (confirm(`Supprimer la liste "${l.name}" ?`)) del.mutate(l.id); }}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                    {l.kind !== "manual" && (
                      <span className="text-xs text-muted-foreground">Auto</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing.id ? "Modifier la liste" : "Nouvelle liste"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => save.mutate()} disabled={!editing.name}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageListMembersDialog list={managingList} onClose={() => setManagingList(null)} />
    </>
  );
}

function ManageListMembersDialog({ list, onClose }: { list: MailingList | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const { data: members = [] } = useMembersWithStatus();

  const { data: listMembers = [] } = useQuery({
    queryKey: ["mailing_list_members", list?.id],
    queryFn: async () => {
      if (!list) return [];
      return ((await supabase.from("mailing_list_members").select("member_id").eq("list_id", list.id)).data ?? []) as { member_id: string }[];
    },
    enabled: !!list,
  });

  const memberIds = useMemo(() => new Set(listMembers.map((x) => x.member_id)), [listMembers]);

  const toggle = useMutation({
    mutationFn: async (vars: { memberId: string; add: boolean }) => {
      if (!list) return;
      if (vars.add) {
        const { error } = await supabase.from("mailing_list_members").insert({ list_id: list.id, member_id: vars.memberId });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("mailing_list_members").delete().eq("list_id", list.id).eq("member_id", vars.memberId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mailing_list_members", list?.id] });
      qc.invalidateQueries({ queryKey: ["mailing_list_counts"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const filtered = members.filter((m: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${m.first_name} ${m.last_name} ${m.email ?? ""}`.toLowerCase().includes(q);
  });

  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Membres de « {list?.name} »</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="text-sm text-muted-foreground">
            <Badge variant="secondary">{memberIds.size}</Badge> adhérent{memberIds.size > 1 ? "s" : ""} dans la liste
          </div>
          <div className="max-h-96 overflow-y-auto border rounded-md divide-y">
            {filtered.map((m: any) => {
              const inList = memberIds.has(m.id);
              return (
                <label key={m.id} className="flex items-center gap-2 text-sm py-2 px-3 cursor-pointer hover:bg-muted/40">
                  <Checkbox
                    checked={inList}
                    onCheckedChange={(v) => toggle.mutate({ memberId: m.id, add: !!v })}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">
                      {m.first_name} {m.last_name}
                      {m._isInactive && <Badge variant="outline" className="ml-1 text-[10px]">inactif</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{m.email ?? "Pas d'email"}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ id?: string; name: string; subject: string; body_html: string }>({ name: "", subject: "", body_html: "<p></p>" });

  const { data: templates = [] } = useQuery({
    queryKey: ["email_templates"],
    queryFn: async () => (await supabase.from("email_templates").select("*").order("name")).data ?? [],
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editing.id) {
        const { error } = await supabase.from("email_templates").update({ name: editing.name, subject: editing.subject, body_html: editing.body_html }).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("email_templates").insert({ name: editing.name, subject: editing.subject, body_html: editing.body_html });
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_templates"] }); toast.success("Modèle enregistré"); setOpen(false); },
    onError: (e: any) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("email_templates").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_templates"] }); toast.success("Supprimé"); },
  });

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing({ name: "", subject: "", body_html: "<p></p>" }); setOpen(true); }}><Plus className="h-4 w-4 mr-2" />Nouveau modèle</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Objet</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {templates.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Aucun modèle</TableCell></TableRow>}
            {templates.map((t: any) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell className="text-muted-foreground">{t.subject}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setOpen(true); }}>Modifier</Button>
                  <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer ce modèle ?")) del.mutate(t.id); }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing.id ? "Modifier le modèle" : "Nouveau modèle"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom du modèle *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Objet *</Label><Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contenu *</Label><RichTextEditor value={editing.body_html} onChange={(html) => setEditing({ ...editing, body_html: html })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => save.mutate()} disabled={!editing.name || !editing.subject}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function statusBadge(status: string) {
  if (status === "sent") return <Badge variant="default">Envoyé</Badge>;
  if (status === "failed") return <Badge variant="destructive">Échec</Badge>;
  if (status === "sending") return <Badge variant="secondary">En cours</Badge>;
  if (status === "draft") return <Badge variant="outline">Brouillon</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function useCampaigns() {
  return useQuery<EmailCampaign[]>({
    queryKey: ["email_campaigns"],
    queryFn: async () =>
      ((await supabase.from("email_campaigns").select("*").order("created_at", { ascending: false }).limit(200)).data ?? []) as any,
  });
}

function DraftsTab({ onEdit }: { onEdit: (c: EmailCampaign) => void }) {
  const qc = useQueryClient();
  const { data: campaigns = [] } = useCampaigns();
  const drafts = campaigns.filter((c) => c.status === "draft");

  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/email-campaigns/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Brouillon supprimé"); },
    onError: (e: any) => toast.error(e.message),
  });

  const sendNow = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ send_error: string | null }>(`/api/email-campaigns/${id}/send`);
      if ((res as any)?.send_error) throw new Error((res as any).send_error);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Brouillon envoyé"); },
    onError: (e: any) => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.error(e.message || "Échec de l'envoi"); },
  });

  return (
    <Card>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Objet</TableHead><TableHead>Destinataires</TableHead><TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {drafts.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Aucun brouillon</TableCell></TableRow>}
          {drafts.map((c) => {
            const rcpts = normalizeRecipients(c.recipient_emails);
            return (
              <TableRow key={c.id}>
                <TableCell>{new Date(c.created_at).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell className="font-medium">{c.subject || <em className="text-muted-foreground">(sans objet)</em>}</TableCell>
                <TableCell>{rcpts.length}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(c)}>Reprendre</Button>
                  <Button variant="ghost" size="sm" onClick={() => sendNow.mutate(c.id)} disabled={rcpts.length === 0 || sendNow.isPending}>
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer ce brouillon ?")) del.mutate(c.id); }}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function HistoryTab() {
  const qc = useQueryClient();
  const { data: campaigns = [] } = useCampaigns();
  const [filter, setFilter] = useState<"all" | "sent" | "failed">("all");
  const [detail, setDetail] = useState<EmailCampaign | null>(null);

  const items = campaigns
    .filter((c) => c.status !== "draft")
    .filter((c) => filter === "all" ? true : c.status === filter);

  const failedCount = campaigns.filter((c) => c.status === "failed").length;

  const retry = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ send_error: string | null }>(`/api/email-campaigns/${id}/send`);
      if ((res as any)?.send_error) throw new Error((res as any).send_error);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Envoi réussi"); },
    onError: (e: any) => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.error(e.message || "Nouvel échec"); },
  });

  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/email-campaigns/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Supprimé"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex gap-2">
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>Tous</Button>
          <Button size="sm" variant={filter === "sent" ? "default" : "outline"} onClick={() => setFilter("sent")}>Envoyés</Button>
          <Button size="sm" variant={filter === "failed" ? "default" : "outline"} onClick={() => setFilter("failed")}>
            Anomalies {failedCount > 0 && <Badge variant="destructive" className="ml-1.5">{failedCount}</Badge>}
          </Button>
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Objet</TableHead><TableHead>Type</TableHead>
            <TableHead>Destinataires</TableHead><TableHead>Statut</TableHead><TableHead>Anomalie</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucune campagne</TableCell></TableRow>}
            {items.map((c) => {
              const rcpts = normalizeRecipients(c.recipient_emails);
              return (
                <TableRow key={c.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {new Date(c.sent_at ?? c.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  </TableCell>
                  <TableCell className="font-medium">{c.subject}</TableCell>
                  <TableCell>{c.is_mass_mailing ? "Newsletter" : "Personnalisé"}</TableCell>
                  <TableCell>{rcpts.length}</TableCell>
                  <TableCell>{statusBadge(c.status)}</TableCell>
                  <TableCell className="max-w-xs">
                    {c.last_error ? (
                      <span className="text-xs text-destructive truncate block" title={c.last_error}>{c.last_error}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(c)} title="Détails">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {c.status === "failed" && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => retry.mutate(c.id)} disabled={retry.isPending} title="Réessayer">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer cette campagne en échec ?")) del.mutate(c.id); }}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.subject}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-3 text-muted-foreground">
                <span>Statut : {statusBadge(detail.status)}</span>
                <span>Créé le {new Date(detail.created_at).toLocaleString("fr-FR")}</span>
                {detail.sent_at && <span>Envoyé le {new Date(detail.sent_at).toLocaleString("fr-FR")}</span>}
              </div>
              {detail.last_error && (
                <div className="border border-destructive/40 bg-destructive/5 rounded-md p-3">
                  <div className="font-medium text-destructive mb-1">Anomalie</div>
                  <div className="text-xs whitespace-pre-wrap break-words">{detail.last_error}</div>
                </div>
              )}
              <div>
                <div className="font-medium mb-1">Destinataires ({normalizeRecipients(detail.recipient_emails).length})</div>
                <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto border rounded p-2 break-all">
                  {normalizeRecipients(detail.recipient_emails).join(", ") || "—"}
                </div>
              </div>
              <div>
                <div className="font-medium mb-1">Contenu</div>
                <div className="border rounded p-3 max-h-72 overflow-y-auto prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: detail.body_html }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
